import Bullets from '@atoms/Bullets/Bullets';
import ConditionalLinkWrapper from '@atoms/ConditionalLinkWrapper/ConditionalLinkWrapper';
import Icon from '@atoms/Icon/Icon';
import ProfilePicture from '@atoms/ProfilePicture/ProfilePicture';
import Tooltip from '@atoms/Tooltip/Tooltip';
import { IAuthor } from '@models/IAuthor';
import ProfilePictureSizeEnum from '@models/enums/ProfilePictureSizeEnum';
import SizeEnum from '@models/enums/SizeEnum';
import TooltipPositionEnum from '@models/enums/TooltipPositionEnum';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';

interface Props extends IAuthor {
  readonly backgroundColourClassName?: string;
  readonly size?:
    | ProfilePictureSizeEnum.Size50
    | ProfilePictureSizeEnum.Size76
    | ProfilePictureSizeEnum.Size120;
  readonly textSize?: SizeEnum;
  readonly isHero?: boolean;
  readonly onAuthorLinkClick?: () => void;
}

const AuthorPanel = ({
  ambassador,
  authorName,
  backgroundColourClassName = 'bg-grey-100 dark:bg-grey-600',
  iconTooltip,
  initials,
  isHero,
  link,
  size,
  specialism,
  subTitle,
  textSize = SizeEnum.Medium,
  thumbnail,
  onAuthorLinkClick,
}: Props) => {
  const isBelowLargeDesktop = useMediaQuery(1440);

  const displayedSpecialisms = [...[subTitle], ...(specialism ?? [])];
  const pictureSize = isBelowLargeDesktop
    ? ProfilePictureSizeEnum.Size76
    : ProfilePictureSizeEnum.Size120;
  const titleFontSize = {
    [SizeEnum.Small]: 'text-md',
    [SizeEnum.Medium]: 'h6',
    [SizeEnum.Large]: 'h5',
    [SizeEnum.XLarge]: 'h1',
  };
  const TitleTag = isHero ? 'h1' : 'p';

  return (
    <div
      className={classNames(
        textSize === SizeEnum.Small ? 'gap-5' : 'gap-5 md:gap-10',
        textSize === SizeEnum.XLarge ? 'flex-col' : 'flex-row items-center',
        'flex sm:rounded-lg'
      )}
      data-testid="Author-Panel"
    >
      {(thumbnail || initials) && (
        <ConditionalLinkWrapper
          link={link}
          linkCondition={!isHero}
          onLinkClick={onAuthorLinkClick}
        >
          <ProfilePicture
            backgroundColourClassName={backgroundColourClassName}
            displayCheck={ambassador}
            displayOutline={ambassador}
            iconToolTip={iconTooltip}
            initials={initials}
            size={size || pictureSize}
            thumbnail={thumbnail}
          />
        </ConditionalLinkWrapper>
      )}

      <div className="flex flex-col">
        {authorName && (
          <div className="flex items-center">
            <TitleTag
              className={classNames(
                textSize && titleFontSize[textSize],
                ambassador ? 'dark:text-yellow' : 'dark:text-white',
                'text-black-100'
              )}
              data-testid="Author-Panel-Author-Name"
            >
              <ConditionalLinkWrapper
                link={link}
                linkCondition={!isHero}
                hasHoverUnderline
                className="inline-block"
                onLinkClick={onAuthorLinkClick}
              >
                {authorName}
              </ConditionalLinkWrapper>
            </TitleTag>

            {ambassador && !(thumbnail || initials) && (
              <div className="tooltip-parent relative ml-3 flex justify-center">
                <Icon
                  name="check"
                  colorClassName="text-black-100 bg-yellow rounded-full p-[1px]"
                  size="1.25rem"
                />
                {iconTooltip && (
                  <Tooltip
                    label={iconTooltip}
                    position={TooltipPositionEnum.Top}
                  />
                )}
              </div>
            )}
          </div>
        )}

        <Bullets
          bullets={displayedSpecialisms}
          textSize={textSize === SizeEnum.XLarge ? 'body-one' : 'body-two'}
          dataTestId="Author-Panel-Bullets"
        />
      </div>
    </div>
  );
};

export default AuthorPanel;
